import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
    autoSearchSelector,
    GroupeTravailUtilisateurApiObject,
    useUtilisateurService,
    useUtilisateursServiceSelector,
    UtilisateurApiObject,
} from '@europrocurement/l2d-domain';

import { GroupesDeTravailJsonld as GroupeTravailDTO } from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';

import { SearchBar, CenterCircularProgress, useModal } from '@europrocurement/flexy-components';
import { useDebouncedCallback } from 'use-debounce';
import { Divider, Typography } from '@mui/material';
import { Box, useTheme } from '@mui/system';
import { enqueueSnackbar } from 'notistack';
import { SubmitHandler } from 'react-hook-form';
import { format } from 'date-fns';
import { AppDispatch } from '../../../../../redux';
import { formatPhoneNumber } from '../../../functions/formatPhoneNumber';
import { SearchMembersWidget } from './ModalWidget/SearchMembersWidget';
import { WorkGroupCreateInfosForm } from './WorkGroupCreateInfosForm';

// Infos Form values type
type ReturnedFormData = Pick<GroupeTravailUtilisateurApiObject, 'lib' | 'email' | 'phone'> & {
    domaine: { label: string; value: string };
    dCloture: Date | null;
    dCreation: Date;
};

/**
 * Workgroup Update infos form
 *
 * Lot of Async here! Be careful while modifying...
 */
export const WorkGroupCreateForm: React.FunctionComponent = function () {
    // Standard hook
    const dispatch = useDispatch<AppDispatch>();
    const theme = useTheme();
    const { modalActions } = useModal();

    // Selectors
    const searchDataSource = useUtilisateursServiceSelector(autoSearchSelector);

    // Services
    const { getAutoSearch, getGroupesTravail, groupesTravailApi } = useUtilisateurService();

    // SearchBar state that will determine multifieldsearch value
    const [searchBarValue, setSearchBarValue] = useState<string>('');
    const [searchReady, setSearchReady] = useState<boolean>(false);

    // Other States
    const [selectedCandidate, setSelectedCandidate] = useState<UtilisateurApiObject | null>(null);

    // Apply standard filters parameters on mount. They should not be modified.
    useEffect(() => {
        // Reset/clean search parameters.
        dispatch({
            type: `${searchDataSource.slicename}/reset${searchDataSource.name}Pagination`,
            payload: {},
        });
        dispatch({
            type: `${searchDataSource.slicename}/reset${searchDataSource.name}Filter`,
            payload: {},
        });
        dispatch({
            type: `${searchDataSource.slicename}/reset${searchDataSource.name}Search`,
            payload: {},
        });

        // Set filters to fetch active only inscriptions full list
        dispatch({
            type: `${searchDataSource.slicename}/set${searchDataSource.name}Pagination`,
            payload: { page: 0, itemsPerPage: 5 },
        });
        dispatch({
            type: `${searchDataSource.slicename}/set${searchDataSource.name}Filter`,
            payload: { key: 'isDeleted', value: false },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Dispatch the search value with delay
    const delayedSearch = useDebouncedCallback((searchValue: string) => {
        // Must use search as argument here or it will NOT be recognized by store.
        dispatch({
            type: `${searchDataSource.slicename}/set${searchDataSource.name}Search`,
            payload: { search: searchValue }, // SET SEARCH STATE VALUE HERE
        });
    }, 1000);

    /**
     * Check if searchBarValue is the same that dataSource.search
     * Needed to avoid async issues before calling getAutoSearch.
     */
    useEffect(() => {
        if (searchDataSource.search !== '' && searchDataSource.search === searchBarValue) {
            setSearchReady(true);
        } else if (searchReady !== false) {
            setSearchReady(false);
            // reset store data to avoid showing old search items
            dispatch({
                type: `${searchDataSource.slicename}/clear${searchDataSource.name}Data`,
                payload: {},
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchDataSource.search, searchBarValue]);

    // Launch search if conditions are met.
    useEffect(() => {
        if (searchReady) {
            dispatch(getAutoSearch({}));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchReady]);

    const checkCandidatesId = (candidateId: number): boolean => {
        let mustBeShown: boolean = true;
        if (selectedCandidate?.id === candidateId) {
            mustBeShown = false;
        }
        return mustBeShown;
    };

    /**
     * Worgroup Create Function
     */
    const handleCreateWorkGroup: SubmitHandler<ReturnedFormData> = (data) => {
        // Handle no selection and inscriptions fetch issues.
        if (!selectedCandidate) {
            enqueueSnackbar('Aucun référent sélectionné', { variant: 'error' });
            return;
        }

        if (data.dCreation && data.dCloture && data.dCloture < data.dCreation) {
            enqueueSnackbar('La date de fin ne peut être inférieure à celle de début', {
                variant: 'error',
            });
            return;
        }

        const newWorkgroup: GroupeTravailDTO = {
            lib: data.lib,
            domaine: data.domaine.value,
            iduserReferent: selectedCandidate['@id'],
            fDelete: false,
            phone: data.phone?.trim() === '' ? null : formatPhoneNumber(data.phone),
            email: data.email?.trim() === '' ? null : data.email,
            dCreation: format(data.dCreation, 'yyyy-MM-dd'),
            dCloture: data.dCloture ? format(data.dCloture, 'yyyy-MM-dd') : null,
            inscriptions: [],
        };

        const addWorkGroup = () => {
            groupesTravailApi
                .apiGroupestravailPost({
                    groupesDeTravailJsonld: newWorkgroup,
                })
                .then(() => {
                    // Close form modal.
                    modalActions.close();

                    // Add notification to user.
                    enqueueSnackbar('Le groupe de travail a été créé avec succès.', {
                        variant: 'success',
                    });

                    // Update store.
                    dispatch(getGroupesTravail({}));
                })
                .catch((error) => {
                    console.error(error);
                    const errorMessage = error.response?.data?.exception?.message;
                    enqueueSnackbar(`Une erreur est survenue: ${errorMessage}`, {
                        variant: 'error',
                    });
                });
        };
        addWorkGroup();
    };

    return (
        <div
            style={{
                width: '50vw',
                height: '70vh',
            }}
        >
            {/* Header section */}
            <Typography
                display="flex"
                alignItems="center"
                width="100%"
                color={theme.palette.secondary.main}
                paddingBottom={1}
                variant="h4"
            >
                Nouveau Groupe de travail
            </Typography>

            <Divider
                sx={{
                    backgroundColor: theme.palette.secondary.main,
                    borderWidth: '1px',
                    marginBottom: '8px',
                }}
            />

            {/* Body section */}
            <Box
                display="flex"
                justifyContent="space-between"
                flexWrap="wrap"
                width="100%"
            >
                {/* User search container */}
                <Box
                    style={{
                        width: '48%',
                        // border: '1px solid blue'
                    }}
                >
                    <Typography
                        variant="l2dh7"
                        sx={{ lineHeight: '1.4', marginTop: '8px' }}
                    >
                        Recherche de référent :
                    </Typography>

                    <Typography
                        variant="l2dbodyxs"
                        color={theme.palette.text.secondary}
                    >
                        Recherchez un utilisateur par nom, prénom ou mail... Cliquez pour le
                        sélectionner.
                    </Typography>

                    <Box sx={{ width: '100%', marginTop: '20px' }} />

                    <SearchBar
                        placeholder="Rechercher un utilisateur"
                        onChange={(e) => {
                            setSearchBarValue(e.target.value);
                            delayedSearch(e.target.value);
                        }}
                        value={searchBarValue}
                    />

                    <Box
                        style={{
                            height: '290px',
                            margin: '16px 0px',
                        }}
                    >
                        {searchDataSource.status === 'loading' && (
                            <CenterCircularProgress sx={{ height: '100%' }} />
                        )}

                        {searchDataSource.data &&
                            searchBarValue !== '' &&
                            searchDataSource.status === 'succeeded' &&
                            searchDataSource.data.map(
                                (user) =>
                                    checkCandidatesId(user.id as number) && (
                                        <SearchMembersWidget
                                            key={user.id}
                                            candidate={user}
                                            setCandidate={setSelectedCandidate}
                                            sx={{
                                                ':hover': {
                                                    backgroundColor: theme.palette.grey.A700,
                                                },
                                            }}
                                        />
                                    ),
                            )}
                    </Box>
                </Box>

                {/* Member edit container */}
                <Box
                    style={{
                        width: '48%',
                    }}
                >
                    <Typography
                        variant="l2dh7"
                        sx={{ lineHeight: '1.4', marginTop: '8px' }}
                    >
                        Informations du Groupe :
                    </Typography>

                    <Typography
                        variant="l2dbodyxs"
                        color={theme.palette.text.secondary}
                    >
                        Les champs Référent, Libellé, Domaine et Date de création sont obligatoires.
                    </Typography>

                    <Box
                        sx={{
                            marginTop: '11px', // Pixel-align with search bar
                            width: '100%',
                            height: '95px',
                        }}
                    >
                        <Box
                            sx={{
                                marginBottom: '9px', // Pixel-align with search bar
                                width: '100%',
                            }}
                        >
                            <Typography variant="l2dlabel">Référent *</Typography>
                        </Box>

                        {selectedCandidate ? (
                            <SearchMembersWidget
                                key={selectedCandidate.id}
                                candidate={selectedCandidate}
                            />
                        ) : (
                            <Typography
                                variant="l2dbodyxs"
                                color={theme.palette.text.secondary}
                            >
                                Sélectionnez un référent (obligatoire).
                            </Typography>
                        )}
                    </Box>
                    <Box
                        sx={{
                            marginTop: '8px',
                            width: '100%',
                        }}
                    >
                        <WorkGroupCreateInfosForm onSubmitCallback={handleCreateWorkGroup} />
                    </Box>
                </Box>
            </Box>
        </div>
    );
};

export default WorkGroupCreateForm;
